import { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import {
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { Animate, ViewContext } from 'components/lib';
import { useNavigate } from 'react-router-dom';
import DateUtils from 'helpers/dateUtils';
import NumberUtils from 'helpers/numberUtils';
import InputUtils from 'helpers/inputUtils';
import { FieldInput } from 'components/fieldInput';
import ResponsiveTable from 'components/ResponsiveTable';
import { policyColumns } from './columns';
import { getLatestSubmissionNumber } from 'helpers/submission';

const dateUtils = new DateUtils();
const numberUtils = new NumberUtils();
const inputUtils = new InputUtils();

export function Policies() {
  const navigate = useNavigate();
  const context = useContext(ViewContext);

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sorting, setSorting] = useState([]);
  const [globalFilter, setGlobalFilter] = useState('');
  const [rowSelection, setRowSelection] = useState({});
  const [columnFilters, setColumnFilters] = useState([]);
  const [companiesOptions, setCompaniesOptions] = useState([]);
  const [companyFilter, setCompanyFilter] = useState([]);
  const [statusFilter, setStatusFilter] = useState([]);
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
    totalItems: 0,
    totalPages: 0,
  });

  async function listPolicies(
    pageIndex,
    pageSize,
    search,
    companyFilter,
    statusFilter
  ) {
    if (loading) return;

    setLoading(true);

    await axios
      .get('/api/policies/search', {
        params: {
          page: pageIndex + 1,
          pageSize: pageSize,
          search,
          companyFilter: companyFilter.length > 0 ? companyFilter : undefined,
          statusFilter: statusFilter.length > 0 ? statusFilter : undefined,
        }
      })
      .then(async (res) => {
        setLoading(false);
        const responseData = res.data.data;

        setCompaniesOptions(
          responseData.data.companies.map((company) => {
            return {
              label: inputUtils
                .capitalizeFirstLetter(company.name)
                .replace('Llc', 'LLC'),
              value: company.id,
            };
          })
        );

        const formattedData = responseData.data.policies.map((value) => {
          const submissionNumber = getLatestSubmissionNumber(value);

          return {
            id: value.id,
            submission_number: submissionNumber,
            'Company Name': inputUtils
              .capitalizeFirstLetter(value.company_name)
              .replaceAll('Llc', 'LLC'),
            effective_date: dateUtils.formatIsoToCommomDate(
              value.effective_date
            ),
            carrier: Array.isArray(value.carrier)
              ? value.carrier.join(', ')
              : (value.carrier || 'N/A'),
            Premium: value.premium
              ? numberUtils.formatToCurrency(value.premium)
              : numberUtils.formatToCurrency(0),
            status:
              value.status === 'bound'
                ? 'active'
                : value.status.replaceAll('_', ' '),
            handleView: (row) => {
              navigate('/app/policy/review', {
                state: {
                  quote_id: row.id,
                  data: {
                    status: row.status,
                    submission_number: submissionNumber,
                  },
                },
              });
            }
          };
        });

        setData(formattedData);
        setPagination(prev => ({
          ...prev,
          totalItems: responseData.rows,
          totalPages: responseData.pages_count,
        }));
      })
      .catch((err) => {
        setLoading(false);
        console.log(err);
        context.notification.show(
          'Failed to load table data',
          'error',
          true,
          'toast',
          'x-circle'
        );
      });
  }

  useEffect(() => {
    listPolicies(
      pagination.pageIndex,
      pagination.pageSize,
      globalFilter,
      companyFilter,
      statusFilter
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    pagination.pageIndex,
    pagination.pageSize,
    globalFilter,
    companyFilter,
    statusFilter
  ]);

  const table = useReactTable({
    data,
    columns: policyColumns,
    state: {
      sorting,
      globalFilter,
      columnFilters,
      rowSelection,
      pagination: {
        pageIndex: pagination.pageIndex,
        pageSize: pagination.pageSize,
      }
    },
    initialState: {
      pagination: {
        pageIndex: pagination.pageIndex,
        pageSize: pagination.pageSize,
      },
    },
    // Pagination
    onPaginationChange: (updater) => {
      const newPagination = typeof updater === 'function' ? updater(pagination) : updater;
      setPagination(newPagination);
    },
    getPaginationRowModel: getPaginationRowModel(),
    manualPagination: true,
    // Column
    onColumnFiltersChange: setColumnFilters,
    // Row
    onRowSelectionChange: setRowSelection,
    getCoreRowModel: getCoreRowModel(),
    enableRowSelection: true,
    // Sort and Filter
    onSortingChange: setSorting,
    onGlobalFilterChange: setGlobalFilter,
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
  });

  // Define filters to pass to ResponsiveTable
  const tableFilters = (
    <div className="p-4 space-y-4">
      <FieldInput
        id="company_filter"
        type="multi_select"
        label="Filter by company"
        isDisabled={loading}
        defaultValue={[]}
        handleChange={(company) => {
          setCompanyFilter(
            company.map((company) => {
              return company.value;
            })
          );
        }}
        options={companiesOptions}
      />
      <FieldInput
        id="status_filter"
        type="multi_select"
        label="Filter by status"
        isDisabled={loading}
        defaultValue={[]}
        handleChange={(status) => {
          setStatusFilter(
            status.map((status) => {
              return status.value;
            })
          );
        }}
        options={[
          { value: 'bound', label: 'Active' },
          { value: 'rejected', label: 'Rejected' },
          { value: 'canceled', label: 'Canceled' },
          {
            value: 'pending_cancelation',
            label: 'Pending Cancellation',
          },
        ]}
      />
    </div>
  );

  return (
    <Animate type="pop">
      <ResponsiveTable
        table={table}
        globalFilter={globalFilter}
        setGlobalFilter={setGlobalFilter}
        loading={loading}
        hasFooter={true}
        hasFilter={true}
        filters={tableFilters}
        totalPages={pagination.totalPages}
        totalItems={pagination.totalItems}
      />
    </Animate>
  );
}
